import { Campaign, Product, User } from 'app/types/authorization'
import { CompanyData, CompanyProfile } from 'app/types/organization'
import { AuthContext } from 'app/utils/context'
import { clientGetIdToken } from 'app/utils/cognito'
import { captureException } from '@sentry/nextjs'
import { useState, useContext, useEffect } from 'react'
import { CheckoutResponse } from 'app/types/checkout'
import { StandardAPIRequest } from 'app/types/api'
import LivedayLoader from './old/animations/LivedayLoader'
import { useTranslation } from 'next-i18next'
import { BillingNoStripe, OrderSummary } from './Billing'
import Currency from 'app/types/currency'
import { CheckoutImage, CompanyLogo } from './old/Image'
import { TFunction } from 'next-i18next'
import Link from 'next/link'
import { formatTimeLeft } from 'app/utils/time'
import {
  SurfboardProvider,
  PaymentElement,
  useSurfboard,
  SurfboardPaymentStatus,
  SurfboardPaymentMethod,
} from './Surfboard'
import React from 'react'

// Component for input field
const InputField: React.FC<{
  id: string
  label: string
  value: string
  onChange: React.ChangeEventHandler<HTMLInputElement>
  onBlur?: () => void
  error?: string
  type?: string
  optional?: boolean
  t: TFunction
}> = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  type = 'text',
  optional = false,
  t,
}) => (
  <div className="sm:col-span-2">
    <label htmlFor={id} className="block text-sm font-medium text-gray-700">
      {label} {optional && `(${t('optional')})`}
    </label>
    <div className="mt-1">
      <input
        type={type}
        id={id}
        name={id}
        autoComplete={id}
        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      {error && <p className="mt-1 text-red-500">{error}</p>}
    </div>
  </div>
)

const PaymentModal = ({
  eventId,
  organization,
  closeModal,
  setShowFailureMessage,
  email,
  phoneNumber,
  billingName,
  billingAddress,
  billingCity,
  billingPostalCode,
  setFinished,
}: {
  eventId: string
  organization: string
  open: boolean
  closeModal: () => void
  setShowFailureMessage: (message: string) => void
  checkoutSession: CheckoutResponse
  email: string
  phoneNumber: string
  billingName: string
  billingAddress: string
  billingCity: string
  billingPostalCode: string
  setFinished: (finished: boolean) => void
}) => {
  const { t } = useTranslation('common')
  const {
    supportedPaymentMethods,
    initiatePayments,
    addCustomerInformationOnce,
    setErrorCallback,
    setPaymentStatusCallback,
    usedPaymentMethod,
    isReady,
  } = useSurfboard()
  const [supportedMethods, setSupportedMethods] = useState<
    SurfboardPaymentMethod[]
  >([])
  const [swishQRData, setSwishQRData] = useState<string | null>(null)
  const [paymentStatus, setPaymentStatus] = useState<
    SurfboardPaymentStatus | 'INITIAL'
  >('INITIAL')
  const [callbackUrl, setCallbackUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!isReady) return

    // Get supported payment methods
    setSupportedMethods(supportedPaymentMethods)

    // Set up callbacks once
    setErrorCallback((code: string, message: string) => {
      const errorMessage = `Error [${code}]: ${message}`
      console.error(errorMessage)

      switch (code) {
        case 'ON_008':
          setShowFailureMessage('Please provide complete customer information')
          break
        case 'ON_009':
          setShowFailureMessage('Phone number is required for Swish payment')
          break
        case 'ON_016':
          setShowFailureMessage(
            'Invalid card details. Please check and try again'
          )
          break
        case 'ON_017':
          setShowFailureMessage('Email is required for this payment method')
          break
        case 'ON_018':
          setShowFailureMessage(
            'Billing address is required for this payment method'
          )
          break
        default:
          setShowFailureMessage(message)
      }
    })

    setPaymentStatusCallback(
      (data: {
        paymentStatus: SurfboardPaymentStatus
        failureReason?: string
      }) => {
        console.log(`Payment status update: ${JSON.stringify(data)}`)
        setPaymentStatus(data.paymentStatus)
        switch (data.paymentStatus) {
          case 'PAYMENT_COMPLETED':
            setFinished(true)
            closeModal()
            break
          case 'PAYMENT_FAILED':
            setShowFailureMessage(data.failureReason || 'Payment failed')
            break
          case 'PAYMENT_CANCELLED':
            setShowFailureMessage('Payment was cancelled')
            break
          case 'PENDING':
            console.log('Payment is pending')
            break
          case 'PAYMENT_INITIATED':
            console.log('Payment initiated')
            break
          case 'PAYMENT_PROCESSING':
            console.log('Payment is processing')
            break
          case 'PAYMENT_PROCESSED':
            console.log('Payment has been processed')
            break
        }
      }
    )

    // Add customer information once
    let formattedPhoneNumber = phoneNumber
    if (phoneNumber.startsWith('07')) {
      formattedPhoneNumber = '+46' + phoneNumber.substring(1)
    }

    const phoneCountryCode = formattedPhoneNumber.startsWith('+46')
      ? '+46'
      : '+358'
    const countryCode = formattedPhoneNumber.startsWith('+46') ? 'SE' : 'FI'
    const cleanPhoneNumber = formattedPhoneNumber.replace(/^\+46|\+358/, '')
    const finalPhoneNumber = formattedPhoneNumber.startsWith('+46')
      ? '0' + cleanPhoneNumber
      : cleanPhoneNumber

    addCustomerInformationOnce({
      phone: { countryCode: phoneCountryCode, number: finalPhoneNumber },
      name: billingName,
      email: email,
      billingAddress: {
        city: billingCity,
        postalCode: billingPostalCode,
        countryCode: countryCode,
        addressLine1: billingAddress,
      },
    })
  }, [
    supportedPaymentMethods,
    initiatePayments,
    addCustomerInformationOnce,
    isReady,
    setErrorCallback,
    setPaymentStatusCallback,
    phoneNumber,
    billingName,
    email,
    billingCity,
    billingPostalCode,
    billingAddress,
    closeModal,
    setFinished,
    setShowFailureMessage,
  ])

  const initiatePayment = async (method: SurfboardPaymentMethod) => {
    try {
      if (!initiatePayments) {
        throw new Error('Payment initiation not supported')
      }
      const paymentAttempt = await initiatePayments(method)

      if (
        method === 'NSWISH' &&
        !/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
      ) {
        const qrData = paymentAttempt.getSwishQRData
        if (qrData) {
          setSwishQRData(qrData)
        }
      } else if (method === 'NSWISH') {
        const redirectUrl = paymentAttempt.getSwishAppRedirectUrl(
          `${process.env.NEXT_PUBLIC_HOST}/orders/complete?event_id=${eventId}&organization=${organization}`
        )
        setCallbackUrl(redirectUrl)
        window.location.href = redirectUrl
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to initiate payment'
      setShowFailureMessage(errorMessage)
    }
  }

  const renderPaymentStatus = () => {
    switch (paymentStatus) {
      case 'PAYMENT_PROCESSING':
        return (
          <div className="flex items-center justify-center gap-x-6 rounded-lg bg-blue-600 px-6 py-2.5 sm:px-3.5">
            <p className="text-sm/6 text-white">
              <strong className="font-semibold">
                {t('processing-payment')}
              </strong>
            </p>
          </div>
        )
      case 'PAYMENT_INITIATED':
        return (
          <div className="flex items-center justify-center gap-x-6 rounded-lg bg-green-600 px-6 py-2.5 sm:px-3.5">
            <p className="text-sm/6 text-white">
              <strong className="font-semibold">
                {t('payment-initiated')}
              </strong>
            </p>
          </div>
        )
      case 'PAYMENT_CANCELLED':
        return (
          <div className="flex items-center justify-center gap-x-6 rounded-lg bg-red-600 px-6 py-2.5 sm:px-3.5">
            <p className="text-sm/6 text-white">
              <strong className="font-semibold">
                {t('payment-cancelled')}
              </strong>
            </p>
          </div>
        )
      case 'PAYMENT_COMPLETED':
        return (
          <div className="flex items-center justify-center gap-x-6 rounded-lg bg-green-600 px-6 py-2.5 sm:px-3.5">
            <p className="text-sm/6 text-white">
              <strong className="font-semibold">
                {t('payment-completed')}
              </strong>
            </p>
          </div>
        )
      case 'PAYMENT_FAILED':
        return (
          <div className="flex items-center justify-center gap-x-6 rounded-lg bg-red-600 px-6 py-2.5 sm:px-3.5">
            <p className="text-sm/6 text-white">
              <strong className="font-semibold">{t('payment-failed')}</strong>
            </p>
          </div>
        )
      case 'PAYMENT_PROCESSED':
        return (
          <div className="flex items-center justify-center gap-x-6 rounded-lg bg-green-600 px-6 py-2.5 sm:px-3.5">
            <p className="text-sm/6 text-white">
              <strong className="font-semibold">
                {t('payment-processed')}
              </strong>
            </p>
          </div>
        )
      default:
        return null
    }
  }

  const renderPaymentContent = () => {
    return (
      <>
        {paymentStatus === 'PAYMENT_PROCESSING' && (
          <React.Fragment>
            {usedPaymentMethod === 'SSWISH' ? (
              <div className="mt-4">
                <h3 className="mb-4 text-lg font-medium">
                  {t('payment-initiated')}
                </h3>
                <p className="mb-4 text-center text-sm text-gray-600">
                  {t('please-check-your-phone-for-swish-notification')}
                </p>
                {/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && (
                  <div className="flex justify-center">
                    <a
                      href={'swish://'}
                      className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('open-swish-app')}
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center">
                <LivedayLoader />
              </div>
            )}
          </React.Fragment>
        )}

        {paymentStatus === 'PAYMENT_INITIATED' && swishQRData && (
          <div className="mt-4">
            <h3 className="mb-4 text-lg font-medium">
              {t('scan-with-swish-app')}
            </h3>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={swishQRData}
              alt="Swish QR Code"
              className="mx-auto h-64 w-64"
            />
          </div>
        )}

        {paymentStatus === 'PAYMENT_INITIATED' && callbackUrl && (
          <div className="mt-4">
            <h3 className="mb-4 text-lg font-medium">
              {t('trying-to-open-swish-app')}
            </h3>
            <p className="mb-4 text-center text-sm text-gray-600">
              {t('swish-redirect-message')}
            </p>
            <div className="flex justify-center">
              <a
                href={callbackUrl}
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('open-swish-app')}
              </a>
            </div>
          </div>
        )}

        {paymentStatus === 'PAYMENT_INITIATED' &&
          usedPaymentMethod === 'SSWISH' && (
            <div className="mt-4">
              <h3 className="mb-4 text-lg font-medium">
                {t('payment-initiated')}
              </h3>
              <p className="mb-4 text-center text-sm text-gray-600">
                {t('please-check-your-phone-for-swish-notification')}
              </p>
              {/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && (
                <div className="flex justify-center">
                  <a
                    href={'swish://'}
                    className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('open-swish-app')}
                  </a>
                </div>
              )}
            </div>
          )}

        {paymentStatus === 'INITIAL' && (
          <>
            {supportedMethods.includes('NSWISH') && (
              <>
                {swishQRData && (
                  <div className="mt-4">
                    <h3 className="mb-4 text-lg font-medium">
                      {t('scan-with-swish-app')}
                    </h3>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={swishQRData}
                      alt="Swish QR Code"
                      className="mx-auto h-64 w-64"
                    />
                  </div>
                )}
                <div className="mt-4">
                  <button
                    onClick={() => initiatePayment('NSWISH')}
                    className="mt-2 flex w-full items-center justify-center space-x-2 rounded-lg border border-blue-200 bg-blue-50 p-4 transition-colors hover:bg-blue-100"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 420 566"
                      xmlns="http://www.w3.org/2000/svg"
                      className="mr-2"
                    >
                      <defs>
                        <linearGradient
                          x1="80.8150544%"
                          y1="59.6365716%"
                          x2="59.8678229%"
                          y2="8.14952521%"
                          id="linearGradient-1"
                        >
                          <stop stop-color="#EF2131" offset="0%" />
                          <stop stop-color="#FECF2C" offset="100%" />
                        </linearGradient>
                        <linearGradient
                          x1="58.8181173%"
                          y1="0%"
                          x2="9.24395007%"
                          y2="94.0712606%"
                          id="linearGradient-2"
                        >
                          <stop stop-color="#FBC52C" offset="0%" />
                          <stop stop-color="#F87130" offset="26.3921199%" />
                          <stop stop-color="#EF52E2" offset="56.079734%" />
                          <stop stop-color="#661EEC" offset="100%" />
                        </linearGradient>
                        <linearGradient
                          x1="20.5690473%"
                          y1="41.0176198%"
                          x2="40.5972643%"
                          y2="91.2765373%"
                          id="linearGradient-3"
                        >
                          <stop stop-color="#78F6D8" offset="0%" />
                          <stop stop-color="#77D1F6" offset="26.5705044%" />
                          <stop stop-color="#70A4F3" offset="55.447147%" />
                          <stop stop-color="#661EEC" offset="100%" />
                        </linearGradient>
                        <linearGradient
                          x1="42.4403836%"
                          y1="100%"
                          x2="91.6136906%"
                          y2="6.27757791%"
                          id="linearGradient-4"
                        >
                          <stop stop-color="#536EED" offset="0%" />
                          <stop stop-color="#54C3EC" offset="24.6657524%" />
                          <stop stop-color="#64D769" offset="56.4199509%" />
                          <stop stop-color="#FECF2C" offset="100%" />
                        </linearGradient>
                      </defs>
                      <g
                        id="01-Primary-(Vertical)/Colour/Light-background/Swish_Logo_Primary_Light-BG"
                        stroke="none"
                        stroke-width="1"
                        fill="none"
                        fill-rule="evenodd"
                      >
                        <g
                          id="Wordmark/Swish"
                          transform="translate(54.000000, 470.000000)"
                          fill="#4A4A49"
                          fill-rule="nonzero"
                        >
                          <path
                            d="M327.407874,15.8757003 C327.407874,13.8495723 327.781624,11.9501057 328.529134,10.1772437 C329.276644,8.40438161 330.291107,6.85814918 331.572553,5.53849999 C332.854,4.21885079 334.355672,3.17914278 336.077615,2.41934476 C337.799559,1.65954674 339.648284,1.27965343 341.623847,1.27965343 C343.626107,1.27965343 345.501529,1.65954674 347.250169,2.41934476 C348.998809,3.17914278 350.520503,4.21885079 351.815298,5.53849999 C353.110093,6.85814918 354.13123,8.40438161 354.87874,10.1772437 C355.62625,11.9501057 356,13.8495723 356,15.8757003 C356,17.9018284 355.62625,19.8012949 354.87874,21.574157 C354.13123,23.347019 353.110093,24.8932515 351.815298,26.2129007 C350.520503,27.5325499 348.998809,28.5722579 347.250169,29.3320559 C345.501529,30.0918539 343.626107,30.4717472 341.623847,30.4717472 C339.648284,30.4717472 337.799559,30.0918539 336.077615,29.3320559 C334.355672,28.5722579 332.854,27.5325499 331.572553,26.2129007 C330.291107,24.8932515 329.276644,23.347019 328.529134,21.574157 C327.781624,19.8012949 327.407874,17.9018284 327.407874,15.8757003 Z M330.331159,15.8757003 C330.331159,17.5552539 330.618145,19.1148159 331.192126,20.5544332 C331.766107,21.9940433 332.560325,23.2470319 333.574803,24.3134151 C334.589281,25.3797983 335.783945,26.2195625 337.15883,26.8327328 C338.533715,27.4459031 340.022039,27.7524837 341.623847,27.7524837 C343.252352,27.7524837 344.760698,27.4459031 346.148931,26.8327328 C347.537165,26.2195625 348.745177,25.3797983 349.773003,24.3134151 C350.80083,23.2470319 351.608396,21.9940433 352.195726,20.5544332 C352.783055,19.1148159 353.076715,17.5552539 353.076715,15.8757003 C353.076715,14.1961468 352.783055,12.6365848 352.195726,11.1969675 C351.608396,9.75735018 350.80083,8.50436873 349.773003,7.43798555 C348.745177,6.37160236 347.537165,5.53183819 346.148931,4.91866786 C344.760698,4.30549753 343.252352,3.99891696 341.623847,3.99891696 C340.022039,3.99891696 338.533715,4.30549753 337.15883,4.91866786 C335.783945,5.53183819 334.589281,6.37160236 333.574803,7.43798555 C332.560325,8.50436873 331.766107,9.75735018 331.192126,11.1969675 C330.618145,12.6365848 330.331159,14.1961468 330.331159,15.8757003 Z M336.738358,9.5974007 C336.738358,8.71763457 337.178848,8.27775811 338.059843,8.27775811 L342.584927,8.27775811 C344.026554,8.27775811 345.174499,8.69097539 346.028796,9.51742236 C346.883094,10.3438693 347.310236,11.4635549 347.310236,12.8765126 C347.310236,13.4630234 347.223473,13.9828774 347.049944,14.4360902 C346.876415,14.8893031 346.656169,15.282526 346.389201,15.6157707 C346.122233,15.9490155 345.828573,16.2222721 345.508211,16.4355487 C345.18785,16.6488253 344.867493,16.7954508 344.547132,16.8754296 L344.547132,16.9554079 C344.573828,16.9820675 344.613873,17.0353858 344.667267,17.1153646 C344.72066,17.1686837 344.774053,17.2553261 344.827447,17.3752942 C344.88084,17.4952623 344.960929,17.635223 345.067717,17.7951805 L347.150056,21.8340866 C347.390327,22.2872995 347.44372,22.6538632 347.310236,22.9337888 C347.176752,23.2137144 346.883092,23.353675 346.429246,23.353675 L345.948706,23.353675 C345.201196,23.353675 344.680616,23.0337649 344.386952,22.393935 L342.064342,17.5152563 L339.581552,17.5152563 L339.581552,22.0740216 C339.581552,22.9271282 339.167758,23.353675 338.340157,23.353675 L337.979753,23.353675 C337.152152,23.353675 336.738358,22.9271282 336.738358,22.0740216 L336.738358,9.5974007 Z M341.984252,15.4358195 C342.758459,15.4358195 343.359128,15.2092164 343.786277,14.7560036 C344.213425,14.3027907 344.426997,13.6763 344.426997,12.8765126 C344.426997,12.1033848 344.213425,11.5035533 343.786277,11.077 C343.359128,10.6504467 342.771807,10.4371733 342.024297,10.4371733 L339.581552,10.4371733 L339.581552,15.4358195 L341.984252,15.4358195 Z M225.25602,24.9972299 C229.78313,24.9972299 233.622238,25.5734014 236.773461,26.7257618 C239.924684,27.8781221 242.387927,28.9418234 244.163264,29.9168975 C245.672301,30.7146854 246.559956,31.7783867 246.826257,33.1080332 C247.092557,34.4376798 246.870643,35.8559481 246.160509,37.3628809 L244.829012,39.7562327 C244.030111,41.3518085 243.009307,42.2825471 241.766571,42.5484765 C240.523836,42.8144058 239.059205,42.5928013 237.372634,41.8836565 C235.863598,41.1745117 234.043905,40.4432171 231.913501,39.6897507 C229.783096,38.9362843 227.297662,38.5595568 224.457123,38.5595568 C221.527817,38.5595568 219.264296,39.1357283 217.666493,40.2880886 C216.068689,41.440449 215.2698,43.0803218 215.2698,45.2077562 C215.2698,47.0692614 216.090881,48.5761715 217.733068,49.7285319 C219.375254,50.8808922 221.461244,51.9002726 223.991099,52.7867036 C226.520954,53.6731346 229.228302,54.6260337 232.113225,55.6454294 C234.998148,56.664825 237.705496,57.9722912 240.235351,59.567867 C242.765206,61.1634429 244.851196,63.2022037 246.493383,65.6842105 C248.135569,68.1662174 248.95665,71.3130003 248.95665,75.1246537 C248.95665,78.1385192 248.357483,80.9085746 247.159131,83.434903 C245.960778,85.9612315 244.229851,88.1551153 241.966296,90.0166205 C239.702741,91.8781257 236.973202,93.3407149 233.777595,94.4044321 C230.581989,95.4681494 227.031368,96 223.125627,96 C217.622082,96 212.984084,95.202224 209.211493,93.6066482 C205.438902,92.0110724 202.531831,90.5041622 200.490194,89.0858726 C198.981157,88.1994416 198.137885,87.0914194 197.960351,85.7617729 C197.782817,84.4321263 198.182262,83.013858 199.158697,81.5069252 L200.756493,79.1135734 C201.732928,77.6952838 202.820306,76.8975078 204.018658,76.7202216 C205.217011,76.5429354 206.65945,76.8975025 208.34602,77.7839335 C209.943824,78.6703645 212.007622,79.6675845 214.537477,80.7756233 C217.067332,81.883662 220.063168,82.4376731 223.525075,82.4376731 C226.454381,82.4376731 228.762285,81.8171807 230.448855,80.5761773 C232.135425,79.3351739 232.978697,77.6509802 232.978697,75.5235457 C232.978697,73.6620406 232.157616,72.1772909 230.51543,71.0692521 C228.873243,69.9612133 226.787253,68.9418329 224.257398,68.0110803 C221.727543,67.0803278 219.020195,66.0831078 216.135272,65.0193906 C213.25035,63.9556734 210.543001,62.6260468 208.013146,61.0304709 C205.483291,59.4348951 203.397301,57.3961343 201.755115,54.9141274 C200.112928,52.4321206 199.291847,49.2410167 199.291847,45.3407202 C199.291847,42.0609254 199.97978,39.1357469 201.355666,36.565097 C202.731552,33.994447 204.595628,31.8670444 206.947949,30.1828255 C209.300271,28.4986065 212.052002,27.2133008 215.203225,26.3268698 C218.354448,25.4404388 221.705346,24.9972299 225.25602,24.9972299 Z M27.336639,24.9972299 C31.8637482,24.9972299 35.7028568,25.5734014 38.8540799,26.7257618 C42.005303,27.8781221 44.4685461,28.9418234 46.2438831,29.9168975 C47.7529195,30.7146854 48.6405747,31.7783867 48.9068752,33.1080332 C49.1731758,34.4376798 48.951262,35.8559481 48.2411272,37.3628809 L46.9096311,39.7562327 C46.1107295,41.3518085 45.089926,42.2825471 43.8471902,42.5484765 C42.6044543,42.8144058 41.1398233,42.5928013 39.4532532,41.8836565 C37.9442167,41.1745117 36.1245237,40.4432171 33.9941193,39.6897507 C31.8637149,38.9362843 29.3782805,38.5595568 26.5377413,38.5595568 C23.6084354,38.5595568 21.3449147,39.1357283 19.7471114,40.2880886 C18.1493082,41.440449 17.3504185,43.0803218 17.3504185,45.2077562 C17.3504185,47.0692614 18.1714995,48.5761715 19.8136862,49.7285319 C21.4558729,50.8808922 23.5418626,51.9002726 26.0717177,52.7867036 C28.6015729,53.6731346 31.3089212,54.6260337 34.1938437,55.6454294 C37.0787663,56.664825 39.7861145,57.9722912 42.3159697,59.567867 C44.8458249,61.1634429 46.9318145,63.2022037 48.5740012,65.6842105 C50.2161879,68.1662174 51.0372689,71.3130003 51.0372689,75.1246537 C51.0372689,78.1385192 50.4381017,80.9085746 49.2397492,83.434903 C48.0413968,85.9612315 46.3104692,88.1551153 44.0469146,90.0166205 C41.78336,91.8781257 39.0538203,93.3407149 35.8582138,94.4044321 C32.6626073,95.4681494 29.1119866,96 25.2062453,96 C19.7027007,96 15.0647025,95.202224 11.2921114,93.6066482 C7.51952039,92.0110724 4.61244972,90.5041622 2.57081222,89.0858726 C1.0617758,88.1994416 0.218503392,87.0914194 0.0409696963,85.7617729 C-0.136564,84.4321263 0.262880825,83.013858 1.23931615,81.5069252 L2.83711143,79.1135734 C3.81354676,77.6952838 4.90092433,76.8975078 6.09927678,76.7202216 C7.29762923,76.5429354 8.74006888,76.8975025 10.426639,77.7839335 C12.0244423,78.6703645 14.0882405,79.6675845 16.6180957,80.7756233 C19.1479508,81.883662 22.143787,82.4376731 25.6056941,82.4376731 C28.5350001,82.4376731 30.8429035,81.8171807 32.5294736,80.5761773 C34.2160437,79.3351739 35.0593162,77.6509802 35.0593162,75.5235457 C35.0593162,73.6620406 34.2382351,72.1772909 32.5960484,71.0692521 C30.9538617,69.9612133 28.8678721,68.9418329 26.3380169,68.0110803 C23.8081618,67.0803278 21.1008135,66.0831078 18.215891,65.0193906 C15.3309684,63.9556734 12.6236201,62.6260468 10.093765,61.0304709 C7.5639098,59.4348951 5.47792016,57.3961343 3.83573348,54.9141274 C2.19354679,52.4321206 1.37246576,49.2410167 1.37246576,45.3407202 C1.37246576,42.0609254 2.06039851,39.1357469 3.43628466,36.565097 C4.8121708,33.994447 6.67624665,31.8670444 9.02856812,30.1828255 C11.3808896,28.4986065 14.1326206,27.2133008 17.2838437,26.3268698 C20.4350668,25.4404388 23.7859651,24.9972299 27.336639,24.9972299 Z M270.12043,0 C273.848637,0 275.712713,1.86147723 275.712713,5.58448753 L275.712713,32.9750693 C275.712713,33.8615003 275.690522,34.6371158 275.646138,35.3019391 C275.601755,35.9667623 275.535181,36.5650943 275.446414,37.0969529 C275.357647,37.7174546 275.313264,38.2493053 275.313264,38.6925208 L275.579564,38.6925208 C276.378465,37.0969449 277.510226,35.4792326 278.974879,33.8393352 C280.439532,32.1994378 282.170459,30.7146881 284.167713,29.3850416 C286.164967,28.055395 288.428488,26.9916937 290.958343,26.1939058 C293.488198,25.3961179 296.217738,24.9972299 299.147044,24.9972299 C306.692226,24.9972299 312.528559,27.0359907 316.656217,31.1135734 C320.783876,35.1911561 322.847674,41.7506473 322.847674,50.7922438 L322.847674,88.8199446 C322.847674,92.5429549 320.983598,94.4044321 317.25539,94.4044321 L311.529957,94.4044321 C307.80175,94.4044321 305.937674,92.5429549 305.937674,88.8199446 L305.937674,54.2493075 C305.937674,50.0830817 305.22755,46.7368547 303.80728,44.2105263 C302.387011,41.6841979 299.546514,40.4210526 295.285705,40.4210526 C292.356399,40.4210526 289.671242,40.9972242 287.230154,42.1495845 C284.789066,43.3019448 282.725268,44.8753363 281.038697,46.8698061 C279.352127,48.8642759 278.042836,51.2354433 277.110784,53.9833795 C276.178732,56.7313157 275.712713,59.7008151 275.712713,62.8919668 L275.712713,88.8199446 C275.712713,92.5429549 273.848637,94.4044321 270.12043,94.4044321 L264.394997,94.4044321 C260.666789,94.4044321 258.802713,92.5429549 258.802713,88.8199446 L258.802713,5.58448753 C258.802713,1.86147723 260.666789,0 264.394997,0 L270.12043,0 Z M181.654029,26.5927978 C185.29347,26.5927978 187.113163,28.454275 187.113163,32.1772853 L187.113163,88.8199446 C187.113163,92.5429549 185.29347,94.4044321 181.654029,94.4044321 L175.795447,94.4044321 C172.156006,94.4044321 170.336313,92.5429549 170.336313,88.8199446 L170.336313,32.1772853 C170.336313,28.454275 172.156006,26.5927978 175.795447,26.5927978 L181.654029,26.5927978 Z M68.6780901,26.5927978 C72.0512303,26.5927978 74.0484544,28.2326706 74.6698223,31.5124654 L85.5880901,71.1357341 C85.7656238,72.1108082 85.9209634,72.9972259 86.0541137,73.7950139 C86.187264,74.5927978 86.3426036,75.3462568 86.5201373,76.0554017 C86.697671,76.8531896 86.8308193,77.6066447 86.9195861,78.3157895 L87.1858853,78.3157895 C87.2746522,77.6066447 87.4078005,76.8531896 87.5853342,76.0554017 C87.7628678,75.3462568 87.9182075,74.5927978 88.0513578,73.7950139 C88.184508,72.9972259 88.3842305,72.1108082 88.650531,71.1357341 L100.101397,31.5124654 C100.722765,28.3213137 102.764372,26.7257618 106.226279,26.7257618 L111.285964,26.7257618 C114.570337,26.7257618 116.611944,28.3213137 117.410846,31.5124654 L128.728562,71.1357341 C128.994863,72.1108082 129.194585,72.9972259 129.327736,73.7950139 C129.460886,74.5927978 129.616226,75.3462568 129.793759,76.0554017 C129.971293,76.8531896 130.104441,77.6066447 130.193208,78.3157895 L130.459507,78.3157895 C130.548274,77.6066447 130.681423,76.8531896 130.858956,76.0554017 C131.03649,75.3462568 131.19183,74.5927978 131.32498,73.7950139 C131.45813,72.9972259 131.657853,72.1108082 131.924153,71.1357341 L142.709271,31.5124654 C143.508173,28.2326706 145.54978,26.5927978 148.834153,26.5927978 L154.959035,26.5927978 C157.000672,26.5927978 158.420921,27.1689693 159.219822,28.3213296 C160.018724,29.47369 160.151872,30.9806001 159.619271,32.8421053 L142.176673,89.7506925 C141.289004,92.8532011 139.203015,94.4044321 135.918641,94.4044321 L126.997618,94.4044321 C123.624477,94.4044321 121.538488,92.8088802 120.739586,89.6177285 L110.487066,55.7119114 C110.220766,54.8254803 109.998852,53.9390626 109.821318,53.0526316 C109.643785,52.1662006 109.466254,51.3684246 109.28872,50.6592798 C109.111186,49.8614919 108.978038,49.1080368 108.889271,48.398892 L108.622972,48.398892 C108.445438,49.1080368 108.267907,49.8614919 108.090374,50.6592798 C107.91284,51.3684246 107.735309,52.1662006 107.557775,53.0526316 C107.380241,53.9390626 107.158328,54.8254803 106.892027,55.7119114 L96.6395074,89.6177285 C95.8406057,92.8088802 93.7989989,94.4044321 90.5146255,94.4044321 L81.3273027,94.4044321 C78.1316961,94.4044321 76.0900892,92.8532011 75.2024208,89.7506925 L57.6266727,32.8421053 C57.0940716,30.9806001 57.2494113,29.47369 58.0926964,28.3213296 C58.9359814,27.1689693 60.3340383,26.5927978 62.286909,26.5927978 L68.6780901,26.5927978 Z"
                            id="swish®"
                          />
                        </g>
                        <g id="Symbol/Colour">
                          <g
                            id="Right-Swirl"
                            transform="translate(119.332186, 49.114212)"
                          >
                            <path
                              d="M0,350.04439 C84.2884683,390.356195 188.31202,370.40599 251.156312,295.526341 C325.643824,206.7744 314.074361,74.4625171 225.315044,-5.68434189e-14 L166.309346,70.306361 C235.651961,128.480254 244.690771,231.848605 186.49762,301.186302 C140.096429,356.473463 63.6990439,374.933385 0,350.04439"
                              id="Base-Shape"
                              fill="url(#linearGradient-1)"
                            />
                            <path
                              d="M1.13686838e-13,350.04439 C84.2884683,390.356195 188.31202,370.40599 251.156312,295.526341 C258.852761,286.356 265.627668,276.719766 271.496605,266.731141 C281.420898,205.05998 259.596059,139.875161 208.256898,96.8056976 C195.213132,85.8627512 181.092937,77.0468488 166.318361,70.3145561 C235.65278,128.489678 244.687902,231.851883 186.49762,301.186302 C140.096429,356.473463 63.6990439,374.933385 1.13686838e-13,350.04439"
                              id="Light-Cast"
                              fill="url(#linearGradient-2)"
                            />
                          </g>
                          <g id="Left-Swirl">
                            <path
                              d="M300.254951,20.4289088 C215.966893,-19.8828961 111.943342,0.0668990353 49.0990489,74.9465478 C-25.3884633,163.698899 -13.8189999,296.010782 74.9399074,370.473299 L133.946015,300.166938 C64.6029903,241.993045 55.5645903,138.624694 113.757742,69.2865868 C160.158932,13.9994259 236.556317,-4.46049609 300.254951,20.4289088"
                              id="Base-shape"
                              fill="url(#linearGradient-3)"
                            />
                            <path
                              d="M300.254951,20.4289088 C215.966893,-19.8828961 111.943342,0.0668990353 49.0990489,74.9465478 C41.4021903,84.117299 34.627283,93.7535332 28.7587562,103.742158 C18.8340538,165.413319 40.6593025,230.597728 91.9984635,273.667601 C105.042229,284.610138 119.162424,293.42645 133.93659,300.158333 C64.6025806,241.983621 55.5670489,138.621416 113.757742,69.2865868 C160.158932,13.9994259 236.556317,-4.46049609 300.254951,20.4289088"
                              id="Shadow"
                              fill="url(#linearGradient-4)"
                            />
                          </g>
                        </g>
                      </g>
                    </svg>
                    <span className="font-medium text-gray-800">{`${t(
                      'pay-with-swish'
                    )} (QR)`}</span>
                  </button>
                </div>
              </>
            )}
            {supportedMethods.includes('SSWISH') && (
              <div className="my-2">
                <PaymentElement type="SSWISH" />
              </div>
            )}

            {supportedMethods.includes('KLARNA') && (
              <div className="my-2">
                <PaymentElement type="KLARNA" />
              </div>
            )}
            <div className="my-2">
              <PaymentElement type="APPLE_PAY" />
            </div>
            <div className="my-4 rounded-lg">
              <PaymentElement type="CARD" />
              <button
                onClick={() => initiatePayment('CARD')}
                className="mt-2 flex w-full items-center justify-center space-x-2 rounded-lg bg-gray-50 p-4 hover:bg-white"
              >
                <span>{t('pay-with-card')}</span>
              </button>
            </div>
          </>
        )}
      </>
    )
  }

  return (
    <div className="w-full">
      {renderPaymentStatus()}
      <div className="sm:flex sm:items-start">
        <div className="mt-3 w-full">
          <div className="mt-4">
            {renderPaymentContent()}

            {paymentStatus === 'INITIAL' && (
              <button
                type="button"
                className="relative my-2 w-full cursor-pointer rounded-md border border-gray-300 bg-gray-800 px-4 py-2 text-center text-sm font-medium text-white shadow-sm"
                onClick={closeModal}
              >
                {t('cancel')}
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <Link href="/privacy-policy" className="text-sm text-gray-500">
          {t('privacy-policy')}
        </Link>
        <span className="mx-4 text-sm text-gray-500">|</span>
        <Link href="/terms_of_service" className="text-sm text-gray-500">
          {t('terms-of-conditions')}
        </Link>
      </div>
    </div>
  )
}

// Props for the CheckoutCart component
interface SurfboardCartProps {
  user: User | undefined
  eventSk: string
  organization: string
  items: Product[]
  campaigns: Campaign[]
  setShowSuccessMessage: React.Dispatch<React.SetStateAction<string>>
  setShowFailureMessage: React.Dispatch<React.SetStateAction<string>>
  companyProfile: CompanyProfile | null
  companyData: CompanyData[]
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
  refetchCart: () => Promise<void>
  name?: string
  email?: string
  phoneNumber?: string
  poolId?: string
  userPoolId?: string
}

export function SurfboardCheckoutCart({
  userPoolId,
  poolId,
  eventSk,
  ...props
}: SurfboardCartProps) {
  const [loading, setLoading] = useState<boolean>(false)
  const [checkoutSession, setCheckoutSession] = useState<
    CheckoutResponse | undefined
  >(undefined)
  const { state } = useContext(AuthContext)
  const { i18n } = useTranslation()

  useEffect(() => {
    const fetchCheckoutSession = async () => {
      setLoading(true)
      try {
        const idToken = await clientGetIdToken()
        let headers: HeadersInit = {
          'Content-Type': 'application/json',
          'Cart-Token': state.cartToken,
        }
        if (idToken) {
          headers.Authorization = `Bearer ${idToken}`
        }
        if (poolId && userPoolId === undefined) {
          headers['Company-Pool-Token'] = poolId
        }
        if (userPoolId && poolId) {
          headers['Public-Pool-Token'] = poolId
          headers['Public-Pool-User-Token'] = userPoolId
        }

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/checkout/${eventSk}/billing_session_surfboard`,
          {
            method: 'PUT',
            headers,
          }
        )

        const checkoutSessionData: StandardAPIRequest<CheckoutResponse> =
          await response.json()
        if (checkoutSessionData.error) {
          throw new Error(checkoutSessionData.error.message)
        }
        setCheckoutSession(checkoutSessionData.data ?? undefined)
      } catch (err) {
        captureException(err)
      } finally {
        setLoading(false)
      }
    }

    fetchCheckoutSession()
  }, [eventSk, state.cartToken, poolId, userPoolId])

  if (checkoutSession === undefined || loading) {
    return <LivedayLoader />
  }

  if (
    !checkoutSession.surfboard?.nonce ||
    !checkoutSession.surfboard?.order_id ||
    !checkoutSession.surfboard?.public_key
  ) {
    return (
      <BillingNoStripe
        setCheckoutSession={setCheckoutSession}
        checkoutSession={checkoutSession}
        eventSk={eventSk}
        currency={
          checkoutSession.checkout_session.cart.currency ?? Currency.SEK
        }
        poolId={poolId}
        {...props}
      />
    )
  }

  return (
    <SurfboardProvider
      publicKey={checkoutSession.surfboard.public_key}
      orderId={checkoutSession.surfboard.order_id}
      nonce={checkoutSession.surfboard.nonce}
      options={{
        locale: i18n.language.substring(0, 2),
      }}
    >
      <InnerSurfboardCheckoutCart
        checkoutSession={checkoutSession}
        eventSk={eventSk}
        poolId={poolId}
        userPoolId={userPoolId}
        {...props}
      />
    </SurfboardProvider>
  )
}

function InnerSurfboardCheckoutCart({
  user,
  eventSk,
  organization,
  setShowFailureMessage,
  companyProfile,
  name: existingName,
  email: existingEmail,
  phoneNumber: existingPhoneNumber,
  poolId,
  checkoutSession,
}: SurfboardCartProps & {
  checkoutSession: CheckoutResponse
}) {
  const { state } = useContext(AuthContext)
  const [finished, setFinished] = useState(false)
  const [open, setOpen] = useState(false)
  const { isReady } = useSurfboard()

  const [email, setEmail] = useState<string>(existingEmail ?? user?.email ?? '')
  const [phoneNumber, setPhoneNumber] = useState<string>(
    existingPhoneNumber ?? user?.phone_number ?? ''
  )
  const [billingName, setBillingName] = useState<string>(
    existingName ?? user?.billing_address?.name ?? ''
  )
  const [billingAddress, setBillingAddress] = useState<string>(
    user?.billing_address?.address ?? ''
  )
  const [billingCity, setBillingCity] = useState<string>(
    user?.billing_address?.city ?? ''
  )
  const [billingPostalCode, setBillingPostalCode] = useState<string>(
    user?.billing_address?.postal_code ?? ''
  )

  const { t } = useTranslation('common')
  const [cartTimeToDelete, setCartTimeToDelete] = useState<number | null>(null)
  const [timeLeft, setTimeLeft] = useState<number | null>(null)

  useEffect(() => {
    if (
      checkoutSession?.checkout_session.cart.time_to_delete !== null &&
      checkoutSession?.checkout_session.cart.time_to_delete !== undefined
    ) {
      const currentTime = Math.floor(new Date().getTime() / 1000)
      const diff =
        checkoutSession.checkout_session.cart.time_to_delete - currentTime
      setCartTimeToDelete(diff > 0 ? diff : null)
      setTimeLeft(diff > 0 ? diff : null)
    }
  }, [checkoutSession?.checkout_session.cart])

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeLeft((timeLeft) =>
        timeLeft !== null
          ? timeLeft > 0
            ? timeLeft - 1
            : timeLeft
          : cartTimeToDelete
      )
    }, 1000)

    return () => clearInterval(intervalId)
  }, [cartTimeToDelete])

  useEffect(() => {
    if (timeLeft === 0) {
      alert(t('time-has-run-out-the-ticket-has-been-released'))
      window.location.reload()
    }
  }, [timeLeft, t])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!isReady) return
    if (email.length === 0) {
      setShowFailureMessage(t('email-is-required') ?? 'email-is-required')
      return
    }
    if (email.length > 0 && !email.includes('@')) {
      setShowFailureMessage(t('invalid-email') ?? 'invalid-email')
      return
    }
    if (phoneNumber.length === 0) {
      setShowFailureMessage(
        t('phone-number-is-required') ?? 'phone-number-is-required'
      )
      return
    }
    if (!phoneNumber.startsWith('+')) {
      setShowFailureMessage(t('include-country-code') ?? 'include-country-code')
      return
    }
    if (billingName.length === 0) {
      setShowFailureMessage(t('name-is-required') ?? 'name-is-required')
      return
    }
    if (billingAddress.length === 0) {
      setShowFailureMessage(t('address-is-required') ?? 'address-is-required')
      return
    }
    if (billingCity.length === 0) {
      setShowFailureMessage(t('city-is-required') ?? 'city-is-required')
      return
    }
    if (billingPostalCode.length === 0) {
      setShowFailureMessage(
        t('postal-code-is-required') ?? 'postal-code-is-required'
      )
      return
    }

    const idToken = await clientGetIdToken()
    try {
      let headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Cart-Token': state.cartToken,
      }
      if (idToken) {
        headers.Authorization = `Bearer ${idToken}`
      }
      if (poolId) {
        headers['Company-Pool-Token'] = poolId
      }
      let response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/checkout/${eventSk}/billing`,
        {
          method: 'PUT',
          body: JSON.stringify({
            email,
            phone_number: phoneNumber,
            shipping_name: billingName,
            shipping_address: billingAddress,
            shipping_city: billingCity,
            shipping_postal_code: billingPostalCode,
            billing_name: billingName,
            billing_address: billingAddress,
            billing_city: billingCity,
            billing_postal_code: billingPostalCode,
            checkout_session_uuid:
              checkoutSession.checkout_session.uuid ?? undefined,
          }),
          headers,
        }
      )
      await response.json()
      setOpen(true)
    } catch (err) {
      captureException(err)
      setShowFailureMessage('')
    }
  }

  return (
    <div className="flex flex-col-reverse bg-white sm:flex-row sm:flex-nowrap">
      <div className="flex h-full w-full flex-row justify-center bg-white px-4 pb-20 sm:w-2/5 sm:px-20">
        {finished ? (
          <div className="my-14">
            <div className="h-20">
              <CompanyLogo imageLink={companyProfile?.logo ?? undefined} />
            </div>
            <div className="mt-8">
              <h2 className="text-3xl font-bold">
                {t('thank-you-for-supporting', {
                  companyName: companyProfile?.name,
                })}
              </h2>
              <p className="mt-4">{t('payment-successful-message')}</p>
              <p className="mt-2 text-xl font-bold">
                {t('cheer-for-company', {
                  companyName: companyProfile?.name,
                })}
              </p>
              <button
                onClick={() => window.location.reload()}
                style={{
                  backgroundColor: companyProfile?.secondary_main ?? undefined,
                }}
                className="mt-5 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-50"
              >
                {t('buy-again')}
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full">
            {open ? (
              <PaymentModal
                eventId={eventSk}
                organization={organization}
                open={open}
                closeModal={() => setOpen(false)}
                setShowFailureMessage={setShowFailureMessage}
                checkoutSession={checkoutSession}
                email={email}
                phoneNumber={phoneNumber}
                billingName={billingName}
                billingAddress={billingAddress}
                billingCity={billingCity}
                billingPostalCode={billingPostalCode}
                setFinished={setFinished}
              />
            ) : (
              <form onSubmit={handleSubmit} className="w-full">
                {timeLeft !== null &&
                  timeLeft > 0 &&
                  checkoutSession.checkout_session.cart.items.filter(
                    (item) => item.Ticket !== undefined
                  ).length > 0 && (
                    <div>
                      <p className="mb-4 text-xs font-bold text-gray-900">
                        {`${t('time-left')}: ${formatTimeLeft(timeLeft, t)}`}
                      </p>
                    </div>
                  )}

                <div>
                  <h3 className="mt-10 text-lg font-medium text-gray-900">
                    {t('contact-information')}
                  </h3>
                  <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <InputField
                      id="name"
                      label={t('name')}
                      value={billingName}
                      onChange={(e) => setBillingName(e.target.value)}
                      t={t}
                    />
                    <InputField
                      id="email"
                      label={t('email')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      type="email"
                      t={t}
                    />
                    <InputField
                      id="phone_number"
                      label={`${t('phone-number')} (${t(
                        'include-country-code'
                      )})`}
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      type="tel"
                      t={t}
                    />
                  </div>
                </div>

                <div className="mt-10">
                  <h3 className="text-lg font-medium text-gray-900">
                    {t('billing-information')}
                  </h3>
                  <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <InputField
                      id="billing-address"
                      label={t('address')}
                      value={billingAddress}
                      onChange={(e) => setBillingAddress(e.target.value)}
                      t={t}
                    />
                    <InputField
                      id="billing-postal-code"
                      label={t('postal-code')}
                      value={billingPostalCode}
                      onChange={(e) => setBillingPostalCode(e.target.value)}
                      t={t}
                    />
                    <InputField
                      id="billing-city"
                      label={t('city')}
                      value={billingCity}
                      onChange={(e) => setBillingCity(e.target.value)}
                      t={t}
                    />
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  <button
                    type="submit"
                    className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-lg font-medium text-gray-900 shadow-sm hover:bg-gray-900 hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t('pay-now')}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>

      <div className="flex w-full flex-row items-center justify-center bg-white sm:w-3/5">
        <div className="relative flex h-full w-full justify-center">
          <div className="absolute inset-0 h-full w-full">
            <CheckoutImage
              relative
              imageLink={companyProfile?.checkout_image ?? undefined}
              alt={''}
            />
          </div>

          <div className="z-10 w-5/6 max-w-2xl">
            <div className="mb-3 mt-12 flex flex-row justify-start">
              <div>
                <p
                  style={{
                    color: companyProfile?.primary_main ?? undefined,
                  }}
                  className="font-bold"
                >
                  {t('your-order')}
                </p>
              </div>
            </div>

            <OrderSummary
              items={checkoutSession.checkout_session.cart.items}
              currency={
                checkoutSession.checkout_session.cart.currency ?? Currency.SEK
              }
              checkoutSession={checkoutSession}
              t={t}
              totalVAT={0}
              ticketVAT={0}
              itemVAT={0}
              kioskVAT={0}
              restaurantVAT={0}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
